import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/shared/PasswordInput';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { api, formatApiError } from '@/lib/api';

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const checks = useMemo(() => ([
    [t('resetPassword.ruleLen', { defaultValue: 'At least 8 characters' }), pw.length >= 8],
    [t('resetPassword.ruleUpper', { defaultValue: 'One uppercase letter' }), /[A-Z]/.test(pw)],
    [t('resetPassword.ruleLower', { defaultValue: 'One lowercase letter' }), /[a-z]/.test(pw)],
    [t('resetPassword.ruleDigit', { defaultValue: 'One number' }), /\d/.test(pw)],
    [t('resetPassword.ruleMatch', { defaultValue: 'Passwords match' }), pw.length > 0 && pw === pw2],
  ]), [pw, pw2, t]);
  const valid = checks.every(([, ok]) => ok);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    setError('');
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-md p-8 shadow-card" data-testid="reset-password-card">
        {done ? (
          <div className="text-center space-y-4" data-testid="reset-password-done">
            <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-600" />
            <h1 className="text-xl font-semibold">{t('resetPassword.doneTitle', { defaultValue: 'Password updated' })}</h1>
            <p className="text-sm text-muted-foreground">{t('resetPassword.doneBody', { defaultValue: 'You can now sign in with your new password.' })}</p>
            <Link to="/login"><Button className="w-full" data-testid="reset-go-login">{t('resetPassword.goLogin', { defaultValue: 'Go to sign in' })}</Button></Link>
          </div>
        ) : !token ? (
          <div className="text-center space-y-3" data-testid="reset-password-notoken">
            <XCircle className="h-10 w-10 mx-auto text-destructive" />
            <p className="text-sm text-muted-foreground">{t('resetPassword.noToken', { defaultValue: 'This reset link is invalid. Request a new one from the sign-in page.' })}</p>
            <Link to="/forgot-password" className="text-sm text-primary underline">{t('resetPassword.requestNew', { defaultValue: 'Request a new link' })}</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5">
            <div>
              <h1 className="text-xl font-semibold">{t('resetPassword.title', { defaultValue: 'Set a new password' })}</h1>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rp-pw">{t('resetPassword.newPw', { defaultValue: 'New password' })}</Label>
              <PasswordInput id="rp-pw" value={pw} autoFocus onChange={(e) => setPw(e.target.value)} autoComplete="new-password" data-testid="reset-password-new" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rp-pw2">{t('resetPassword.confirmPw', { defaultValue: 'Confirm password' })}</Label>
              <PasswordInput id="rp-pw2" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" data-testid="reset-password-confirm" />
            </div>
            <ul className="space-y-1">
              {checks.map(([label, ok]) => (
                <li key={label} className={`text-xs flex items-center gap-1.5 ${ok ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                  {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />} {label}
                </li>
              ))}
            </ul>
            {error && <p className="text-sm text-destructive" data-testid="reset-password-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={!valid || saving} data-testid="reset-password-submit">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('resetPassword.submit', { defaultValue: 'Update password' })}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
